<template>
  <div :class="getContainerClass">
    <v-btn
      :class="[
        'asc-accent-button',
        'asc-accent-button--accent',
        large ? 'v-size--large' : 'v-size--default',
        loading ? 'asc-accent-button--accent--loading' : null,
        !iconOnly && icon
          ? `v-btn--has-icon v-btn--has-icon-${iconPosition}`
          : null,
        customClass,
      ]"
      :disabled="disabled"
      :loading="loading"
      :block="block"
      :icon="iconOnly"
      :outlined="outline"
      :depressed="depressed"
      :ripple="getRippleObj"
      v-bind="bind"
      v-on="on"
      @click="handleClick($event)"
    >
      <template v-if="!iconOnly">
        <v-icon v-if="icon && iconPosition === 'left'" left :size="16">
          {{ icon }}
        </v-icon>
        {{ buttonText }}
        <v-icon v-if="icon && iconPosition === 'right'" right :size="16">
          {{ icon }}
        </v-icon>
      </template>
      <v-icon v-else :size="16">{{ icon }}</v-icon>
    </v-btn>
  </div>
</template>

<script>
export default {
  name: 'AscAccentButton',

  props: {
    buttonText: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    onClick: {
      type: Function,
      default: () => {},
    },
    outline: {
      type: Boolean,
      default: false,
    },
    primary: {
      type: Boolean,
      default: true,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    to: {
      type: [String, Object],
      default: '',
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: undefined,
    },
    iconPosition: {
      type: String,
      validator: function (value) {
        return ['left', 'right'].indexOf(value) !== -1
      },
      default: 'right',
    },
    block: {
      type: Boolean,
      default: false,
    },
    depressed: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customClass: {
      type: String,
      default: '',
    },
    on: {
      type: Object,
      default: null,
    },
    bind: {
      type: Object,
      default: null,
    },
    value: {
      type: [String, Object, Number],
      default: function () {
        return this.buttonText
      },
    },
  },

  computed: {
    getContainerClass() {
      return this.secondary
        ? 'asc-accent-button-container asc-accent-button-container--secondary'
        : 'asc-accent-button-container asc-accent-button-container--primary'
    },
    getRippleObj() {
      return {
        center: true,
        class: this.secondary ? 'ripple--secondary' : 'ripple--primary',
      }
    },
  },

  methods: {
    handleClick(e) {
      if (this.onClick) {
        this.onClick(e, this.value)
      } else {
        if (this.to) {
          if (this.to.match(/^(http(s)?|ftp):\/\//)) {
            // External link
            window.open(this.to, '_blank', 'noopener', 'noreferrer')
          } else {
            // Internal link
            this.$router.push(this.to)
          }
        }
      }
    },
  },
}
</script>

<style lang="scss">
.ripple {
  &--primary {
    color: $accent-500;
  }

  &--secondary {
    color: $accent-400;
  }
}

.asc-accent-button-container {
  padding: 0;
  margin-top: auto;

  & .asc-accent-button {
    &--accent {
      border-radius: 2px;
      text-transform: inherit;
      max-height: 32px !important;
      padding: 6px 15px !important;

      &.v-btn--icon {
        &.v-size--default {
          min-width: 35px !important;
        }

        &.v-size--large {
          min-width: 103px !important;
        }
      }

      &.v-btn--has-icon {
        &:not(.v-btn--block) {
          .v-btn__content {
            .v-icon {
              height: 16px;
              width: 16px;

              &--left {
                margin: 0 9px 0 0;
              }

              &--right {
                margin: 0 0 0 9px;
              }
            }
          }
        }
      }

      &.v-btn--disabled {
        pointer-events: auto;
        cursor: not-allowed;
      }

      .v-btn {
        &__content {
          color: $primary-black;
          font-size: 14px;
          letter-spacing: 0;
          line-height: 18px;
          text-transform: uppercase;
          font-weight: normal;
        }

        &__loader {
          .v-progress-circular {
            height: 14px !important;
            width: 14px !important;

            &__overlay {
              stroke: $primary-black;
            }
          }
        }
      }

      &.theme--light {
        &.v-btn {
          margin: 0;
          padding: 6px 9px !important;

          &--active::before,
          &:hover::before,
          &:focus::before {
            background-color: inherit;
          }

          &.v-btn--block {
            width: 100% !important;
          }
        }
      }
    }
  }

  &--primary {
    .asc-accent-button {
      &--accent {
        &.v-btn {
          background-color: $accent-400;

          &:hover,
          &--loading {
            background-color: $accent-300 !important;
          }

          &--outlined {
            background-color: $primary-white !important;
            border-color: $accent-400 !important;
          }

          &--disabled {
            &.v-btn--has-bg {
              background-color: $accent-200 !important;
              border: 1px solid $accent-200;

              .v-btn__content {
                color: $gray-700;
              }
            }
          }
        }
      }
    }
  }

  &--secondary {
    .asc-accent-button {
      &--accent {
        &.v-btn {
          border: 1px solid $gray-400;
          background-color: $primary-white;

          &:hover,
          &--nohover {
            background-color: none !important;
          }

          &--loading {
            background-color: $accent-100 !important;
          }

          &--disabled {
            &.v-btn--has-bg {
              background-color: $gray-0 !important;
              border: 1px solid $gray-400;

              .v-btn__content {
                color: $gray-700;
              }
            }
          }
        }
      }
    }
  }
}
</style>
